import React, { useState, useEffect, useCallback } from 'react';
import { Elements, useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { CheckCircle, AlertCircle, Zap, MapPin, Briefcase, RefreshCw } from 'react-feather';
import { Navbar } from './Navbar';
import WalletPayButton from './WalletPayButton';
import ModernTemplate from './templates/ModernTemplate';
import ClassicTemplate from './templates/ClassicTemplate';
import MinimalTemplate from './templates/MinimalTemplate';
import ExecutiveTemplate from './templates/ExecutiveTemplate';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const api = process.env.REACT_APP_API_URL;

const TEMPLATES = { modern: ModernTemplate, classic: ClassicTemplate, minimal: MinimalTemplate, executive: ExecutiveTemplate };

const CARD = "bg-white shadow-md rounded-2xl p-6 border border-gray-200";

// ── Credit purchase (must live inside <Elements>) ────────────────────────────
function CreditPurchase({ onPurchased, balance }) {
  const stripe = useStripe();
  const elements = useElements();
  const [price, setPrice] = useState(null);   // cents, from backend
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [enabled, setEnabled] = useState(true);  // false while Auto-Apply is in private beta

  useEffect(() => {
    let alive = true;
    fetch(`${api}payment-config/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!alive || !cfg) return;
        if (cfg.credit_pack) setPrice(cfg.credit_pack);
        if (cfg.autoapply_enabled === false) setEnabled(false);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const createIntent = useCallback(async () => {
    const { data } = await axios.post(`${api}autoapply/credit-pack/intent/`, {}, { withCredentials: true });
    if (data.already_paid) return { id: data.id, amount: data.amount, already_paid: true };
    return { clientSecret: data.clientSecret, id: data.id, amount: data.amount };
  }, []);

  const finalize = useCallback(async (intentId) => {
    const { data } = await axios.post(`${api}autoapply/credit-pack/status/`, { intent_id: intentId }, { withCredentials: true });
    if (data.status === 'succeeded') { toast.success('1,000 Auto-Apply credits added!'); onPurchased(data.balance); }
    else throw new Error('payment not completed');
  }, [onPurchased]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || loading) return;
    setLoading(true); setErr(null);
    try {
      const intent = await createIntent();
      if (intent.already_paid) { await finalize(intent.id); setLoading(false); return; }
      const { error, paymentIntent } = await stripe.confirmCardPayment(intent.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (error) { setErr(error.message); setLoading(false); return; }
      if (paymentIntent.status === 'succeeded') await finalize(intent.id);
    } catch (e2) {
      // Prefer the server's plain-language message over its machine code.
      setErr(e2?.response?.data?.message || e2?.response?.data?.error || 'Payment failed. Please try again.');
    }
    setLoading(false);
  };

  const handleWalletPaid = async (intentId) => {
    setLoading(true);
    try { await finalize(intentId); } finally { setLoading(false); }
  };

  const dollars = price != null ? (price / 100).toFixed(2) : '24.99';

  // Private beta: no payment form for a feature that cannot serve it yet.
  if (!enabled) {
    return (
      <div>
        <h3 className="font-bold text-gray-800 text-lg mb-1">Application credits</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-4 text-sm text-blue-900">
          <p className="font-semibold">Auto-Apply is in private beta.</p>
          <p className="mt-1 text-blue-800">
            You can browse your matched jobs today. Applying, and buying application credits,
            opens up soon, and your account already holds 5 free credits for when it does.
            We will never charge you for something you cannot use yet.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="font-bold text-gray-800 text-lg">Application credits</h3>
        <span className="text-sm text-gray-500">
          Balance: <span className="font-bold text-gray-900">{balance == null ? '…' : balance}</span>
        </span>
      </div>
      <p className="text-gray-500 text-sm mb-4">
        <span className="font-semibold text-gray-700">${dollars}</span> for <span className="font-semibold text-gray-700">1,000 applications</span>, one credit per submitted application. You're only charged when an application is actually sent.
        Every new account starts with <span className="font-semibold text-gray-700">5 free credits</span>.
      </p>

      {price != null && (
        <div className="mb-3">
          <WalletPayButton
            amount={price}
            label="Resume Advisory, 1,000 Auto-Apply credits"
            createIntent={createIntent}
            onPaid={handleWalletPaid}
            disabled={loading || !stripe}
          />
        </div>
      )}

      <div className="border border-gray-200 rounded-xl p-3">
        <CardElement options={{ style: { base: { fontSize: '15px' } } }} />
      </div>

      <button
        type="submit"
        disabled={!stripe || loading}
        className="mt-4 w-full py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-500 disabled:opacity-50 transition"
      >
        {loading ? <ClipLoader color="#fff" size={18} /> : `Buy 1,000 credits, $${dollars}`}
      </button>
      {err && <p className="text-red-500 text-sm mt-2">{err}</p>}
      <p className="mt-3 text-[11px] text-center text-gray-400">
        A credit is charged only when an application is actually sent; failed sends refund
        automatically. By purchasing you agree to the{" "}
        <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>.
      </p>
    </form>
  );
}

// ── Supplemental profile (front-load every question once) ────────────────────
const PROFILE_FIELDS = [
  { key: 'authorized_to_work', label: 'Are you authorized to work in the country of the job?', type: 'bool', required: true },
  { key: 'needs_sponsorship', label: 'Will you now or in the future need visa sponsorship?', type: 'bool', required: true },
  { key: 'country', label: 'Country', type: 'text', required: true, placeholder: 'United States' },
  { key: 'zip', label: 'ZIP / Postal code', type: 'text', required: true, placeholder: '94107' },
  { key: 'earliest_start', label: 'Earliest start date', type: 'text', required: true, placeholder: 'Immediately, or 2026-06-01' },
  { key: 'phone', label: 'Phone (used only if your résumé has none)', type: 'text', required: false, placeholder: '+1 313 555 0100' },
  { key: 'can_relocate', label: 'Are you willing to relocate?', type: 'bool', required: true },
  { key: 'in_person_ok', label: 'Are you open to working onsite / in person?', type: 'bool', required: true },
  { key: 'languages', label: 'Languages you speak', type: 'text', required: true, placeholder: 'English, Spanish' },
  { key: 'years_experience', label: 'Years of professional experience', type: 'text', required: true, placeholder: '3' },
  { key: 'expected_salary', label: 'Expected salary', type: 'text', required: true, placeholder: '$90,000' },
  { key: 'cover_letter', label: 'About you, why you\'re a fit (used to answer "why are you interested" questions autonomously)', type: 'textarea', required: true, placeholder: 'A few sentences in your own words about your background, strengths, and what you\'re looking for. The agent tailors this to each role so it never has to stop and ask you.' },
  { key: 'currently_enrolled', label: 'Are you currently enrolled in a degree program?', type: 'bool', required: false },
  { key: 'graduation_date', label: 'Graduation date', type: 'text', required: false, placeholder: 'May 2026' },
  { key: 'gpa', label: 'GPA', type: 'text', required: false, placeholder: '3.7' },
  { key: 'field_of_study', label: 'Field of study / major', type: 'text', required: false, placeholder: 'Marketing' },
  { key: 'street_address', label: 'Street address', type: 'text', required: false, placeholder: '1 Market St' },
  { key: 'github', label: 'GitHub URL', type: 'text', required: false, placeholder: 'github.com/you' },
  { key: 'website', label: 'Portfolio / website', type: 'text', required: false, placeholder: 'yoursite.com' },
];

// The live stages a run reports (matches Application.STAGES on the backend). A dry
// run (fill) stops after 'reviewing'; 'assist' opens a visible Chrome window, fills
// it, and waits for YOU to press Submit; a real send (submit) presses + confirms.
const STAGE_STEPS = {
  fill: [
    ['preparing', 'Tailoring your résumé to this job'],
    ['opening', "Opening the employer's form"],
    ['filling', 'Filling in your details'],
    ['reviewing', "Answering the employer's questions"],
  ],
  assist: [
    ['preparing', 'Tailoring your résumé to this job'],
    ['opening', "Opening the employer's form in a Chrome window"],
    ['filling', 'Filling in your details'],
    ['reviewing', "Answering the employer's questions"],
    ['awaiting_user', 'Your turn: review the window and press Submit'],
    ['verifying', 'Confirming the employer received it'],
  ],
  submit: [
    ['preparing', 'Tailoring your résumé to this job'],
    ['opening', "Opening the employer's form"],
    ['filling', 'Filling in your details'],
    ['reviewing', "Answering the employer's questions"],
    ['submitting', 'Submitting your application'],
    ['verifying', 'Confirming the employer received it'],
  ],
};

// Does a completed dry run actually still need the user, or is it fully answered?
function appNeedsUser(a) {
  const pv = a.preview || {};
  return (pv.unanswered || []).length > 0 || (pv.agent_answers || []).some((d) => d.review);
}

// A failed run whose only blocker is a question the agent couldn't truthfully
// answer isn't dead, one answer from the user re-dispatches and submits it.
// That's work to do, not a failure, and it must read that way.
function answerableFail(a) {
  const pv = a.preview || {};
  return a.status === 'failed'
    && ['incomplete', 'needs_review'].includes(a.reason)
    && ((pv.unanswered || []).length > 0 || (pv.missing_on_form || []).length > 0
        || (pv.agent_answers || []).some((d) => d.review));
}

// The questions such an application still needs, the form's own authoritative
// list when present (full labels), else the sweep's truncated ones.
function openQuestions(a) {
  const pv = a.preview || {};
  const drafts = new Set((pv.agent_answers || []).filter((d) => d.review).map((d) => d.question));
  const qs = (pv.missing_on_form || []).length ? pv.missing_on_form : (pv.unanswered || []);
  return qs.filter((q) => !drafts.has(q));   // drafted questions render as drafts, not blanks
}

// Agent drafts awaiting the user's approval (shown prefilled, approving without
// editing is one click, and editing wins).
function reviewDrafts(a) {
  return ((a.preview || {}).agent_answers || []).filter((d) => d.review);
}

// Which "All applications" bucket a row falls in, drives the filter pills.
function appBucket(a) {
  if (a.status === 'confirmed' || a.status === 'pressed') return 'submitted';
  if (a.status === 'queued' || a.status === 'held') return 'inflight';
  if (answerableFail(a)) return 'needs';
  if (a.status === 'failed') return 'failed';
  // A finished dry run is 'ready' when nothing is left for the user; only genuinely
  // open questions / review drafts make it 'needs'.
  return appNeedsUser(a) ? 'needs' : 'ready';
}

// Visuals per bucket (a 'filled' row can be ready OR needs-you, status alone is
// not enough, so the table reads the bucket).
const BUCKET_UI = {
  submitted: ['bg-green-500', 'text-green-700', 'Submitted'],
  ready:     ['bg-green-500', 'text-green-700', 'Ready to send'],
  needs:     ['bg-amber-500', 'text-amber-700', 'Needs you'],
  inflight:  ['bg-gray-400', 'text-gray-500', 'In flight'],
  failed:    ['bg-red-500', 'text-red-700', 'Failed'],
};

// Backend/engine failure codes → what actually happened, in plain English. Every
// code the two services can emit must have an entry here, a code without one
// falls through to a generic message rather than showing the raw slug to the user.
const REASON_TEXT = {
  no_resume: "You didn't have a résumé generated yet when this was submitted.",
  no_job: "The job posting this pointed to could no longer be found.",
  missing_answers: 'A required question on the employer form had no answer.',
  unsupported_ats: "We don't support this employer's application platform yet.",
  no_browser: 'The automation service was misconfigured (no browser available).',
  no_credits: "You didn't have a credit available at the moment this was sent.",
  engine_unreachable: "Couldn't reach the automation service, it was down or unreachable at the moment this was sent.",
  engine_crashed: 'The automation service crashed mid-run, we could not confirm what it had done on the employer site before that.',
  already_submitted: 'You had already applied to this posting.',
  // Engine-run outcomes, what actually happened on the employer's site.
  bot_wall: "The employer's site blocked automated access before the form could be reached.",
  captcha: "The employer's form required a CAPTCHA, which needs a human to solve.",
  http_error: "The employer's job page couldn't be loaded (it may have been taken down).",
  form_not_found: "No application form was found on the employer's page, the posting may have closed.",
  incomplete: 'Some required fields on the form could not be filled in.',
  needs_review: 'The run stopped so you could review drafted answers before anything was sent.',
  multi_step: "This employer uses a multi-page application flow we can't complete automatically yet.",
  no_submit_button: "The form's submit button couldn't be found, the page layout may have changed.",
  error: 'The run hit an unexpected error partway through.',
  abandoned: 'The application window was closed (or sat idle) without Submit being pressed, nothing was sent and your credit was refunded.',
  no_credential: 'This employer has not enabled API submissions, and no fallback was possible.',
  bad_credential: "The employer's API credential was rejected, it may have expired.",
  api_error: "The employer's application API returned an error.",
  monthly_cap: 'The monthly AI-answer budget was reached, try again next month or contact support.',
  daily_cap: 'The daily AI-answer budget was reached, try again tomorrow.',
  validation_error: "The employer's form rejected the submission (a field failed their validation), nothing was sent.",
  flagged_spam: "The employer's site flagged the automated submission as spam and rejected it, nothing was sent. This form only accepts a person pressing Submit; the agent filled everything, but the final send needs you.",
  rejected: "The employer's site rejected the submission when it was sent, nothing went through.",
};
function explainReason(reason) {
  if (typeof reason !== 'string' || !reason) return '';
  if (REASON_TEXT[reason]) return REASON_TEXT[reason];
  if (reason.startsWith('dispatch_error:')) return `Something went wrong preparing this application: ${reason.slice('dispatch_error:'.length).trim()}`;
  return reason;   // last resort: show the raw detail rather than hide it
}

// The engine reports filled fields keyed by its internal selector, a camelCase
// key ('firstName') or a matching regex ('/require .*sponsorship|visa sponso').
// Those are debugging labels, not something a user should ever have to read.
const FILLED_LABELS = [
  [/sponsorship|visa/i, 'Visa sponsorship required?'],
  [/linkedin/i, 'LinkedIn'],
  [/github/i, 'GitHub'],
  [/portfolio|website/i, 'Portfolio / website'],
  [/authoriz/i, 'Authorized to work?'],
  [/^resume$/i, 'Résumé'],
];
function prettyFilled(entry) {
  const i = entry.indexOf(' → ');
  if (i < 0) return { label: entry, value: '' };
  let key = entry.slice(0, i).trim();
  const value = entry.slice(i + 3).trim();
  const named = FILLED_LABELS.find(([re]) => re.test(key));
  if (named) return { label: named[1], value };
  if (key.startsWith('/')) {
    // A regex key: keep its first alternative, drop the regex syntax.
    key = key.replace(/^\/|\/i?$/g, '').split('|')[0].replace(/[.*^$?()[\]\\]/g, ' ');
  }
  // camelCase → words; then sentence-case.
  key = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').trim().toLowerCase();
  return { label: key.charAt(0).toUpperCase() + key.slice(1), value };
}

// Scraped JDs arrive with their structure flattened into one wall of text. Put
// the paragraphs back, presentationally only: break ahead of header-shaped
// phrases ("The Opportunity:", "You will:"), then keep paragraphs readable by
// grouping a few sentences at a time. The stored text is never modified.
function formatJd(text) {
  if (!text) return [];
  let t = String(text).replace(/\s+/g, ' ').trim();
  // A break before short Title-Case phrases ending in ':', section headers.
  t = t.replace(/([.!?])\s+((?:[A-Z][\w'’&/-]*\s?){1,6}:)/g, '$1\n\n$2');
  return t.split(/\n\n/).flatMap((block) => {
    const sentences = block.match(/[^.!?]+[.!?]+(?:\s|$)|[^.!?]+$/g) || [block];
    const paras = [];
    for (let i = 0; i < sentences.length; i += 4) paras.push(sentences.slice(i, i + 4).join('').trim());
    return paras;
  }).filter(Boolean);
}
// A paragraph that opens with a short "Header:" gets it styled as a subheading.
function jdParagraph(p, i) {
  const m = p.match(/^((?:[A-Z][\w'’&/-]*\s?){1,6}:)\s*(.*)$/s);
  return (
    <p key={i} className="text-sm text-gray-600 leading-relaxed">
      {m ? (<><span className="font-semibold text-gray-800">{m[1]}</span> {m[2]}</>) : p}
    </p>
  );
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function profileMissing(p) {
  return PROFILE_FIELDS.filter((f) => f.required).filter((f) => {
    const v = p[f.key];
    return f.type === 'bool' ? (v === null || v === undefined) : !String(v || '').trim();
  }).map((f) => f.label);
}

// ── Overview dashboard pieces ────────────────────────────────────────────────

// A headline number with its label, deliberately a stat tile, not a chart.
function StatTile({ label, value, sub, accent = 'text-gray-900' }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
      <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-3xl font-extrabold mt-1 leading-none ${accent}`}>{value}</p>
      {sub && <p className="text-[11px] text-gray-400 mt-1.5">{sub}</p>}
    </div>
  );
}

// GitHub-style activity calendar: one cell per day, last 16 weeks, colored by
// how many applications went out that day. Sequential encoding, one hue,
// light→dark, with a Less→More legend and a per-cell tooltip.
const CAL_SCALE = ['#eef2f7', '#bfdbfe', '#60a5fa', '#2563eb', '#1e40af']; // 0,1,2,3,4+
function ActivityCalendar({ applications }) {
  const DAY = 864e5;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(today.getTime() + (6 - today.getDay()) * DAY);   // this week's Saturday
  const start = new Date(end.getTime() - (16 * 7 - 1) * DAY);           // 16 whole weeks
  const counts = {};
  applications.forEach((a) => {
    const k = new Date(a.created_at).toDateString();
    counts[k] = (counts[k] || 0) + 1;
  });
  const weeks = [];
  for (let w = 0; w < 16; w++) {
    weeks.push([0, 1, 2, 3, 4, 5, 6].map((d) => new Date(start.getTime() + (w * 7 + d) * DAY)));
  }
  const monthOf = (wk) => wk[0].toLocaleDateString(undefined, { month: 'short' });
  return (
    <div className="min-w-[290px]">
      <div className="flex gap-[3px] text-[9px] text-gray-400 mb-1 ml-7">
        {weeks.map((w, i) => (
          <div key={i} className="w-[13px] overflow-visible whitespace-nowrap">
            {i === 0 || monthOf(w) !== monthOf(weeks[i - 1]) ? monthOf(w) : ''}
          </div>
        ))}
      </div>
      <div className="flex gap-[3px]">
        <div className="flex flex-col gap-[3px] text-[9px] text-gray-400 w-6 pr-1 text-right">
          {['', 'Mon', '', 'Wed', '', 'Fri', ''].map((d, i) => (
            <div key={i} className="h-[13px] leading-[13px]">{d}</div>
          ))}
        </div>
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-[3px]">
            {week.map((day, di) => {
              const future = day > today;
              const n = counts[day.toDateString()] || 0;
              return (
                <div
                  key={di}
                  title={future ? undefined : `${day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${n} application${n === 1 ? '' : 's'}`}
                  className="w-[13px] h-[13px] rounded-[3px]"
                  style={{ background: future ? 'transparent' : CAL_SCALE[Math.min(n, 4)] }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-[3px] mt-2 ml-7 text-[10px] text-gray-400">
        <span className="mr-1">Less</span>
        {CAL_SCALE.map((c) => <span key={c} className="w-[11px] h-[11px] rounded-[3px] inline-block" style={{ background: c }} />)}
        <span className="ml-1">More</span>
      </div>
    </div>
  );
}

// Where every application stands, status colors always paired with a label
// and a count, never color alone. 2px gaps separate the segments.
const PIPELINE_BUCKETS = [
  ['submitted', 'Submitted', '#16a34a'],
  ['ready', 'Ready to send', '#0ea5e9'],
  ['needs', 'Needs you', '#d97706'],
  ['inflight', 'In flight', '#9ca3af'],
  ['failed', 'Failed', '#dc2626'],
];
function PipelineBar({ applications }) {
  const counts = Object.fromEntries(PIPELINE_BUCKETS.map(([k]) => [k, 0]));
  applications.forEach((a) => { counts[appBucket(a)] = (counts[appBucket(a)] || 0) + 1; });
  const total = applications.length;
  return (
    <div>
      {total > 0 ? (
        <div className="flex h-3 rounded-full overflow-hidden gap-[2px]">
          {PIPELINE_BUCKETS.map(([k, label, c]) => counts[k] > 0 && (
            <div key={k} title={`${label}: ${counts[k]}`}
                 style={{ width: `${(counts[k] / total) * 100}%`, background: c, minWidth: 10 }} />
          ))}
        </div>
      ) : (
        <div className="h-3 rounded-full bg-gray-100" />
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2.5">
        {PIPELINE_BUCKETS.map(([k, label, c]) => (
          <span key={k} className="flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: c }} />
            {label} <span className="font-semibold text-gray-800">{counts[k]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────
export default function AutoApply() {
  const navigate = useNavigate();
  const loggedIn = !!Cookies.get('access_token');

  const [balance, setBalance] = useState(null);
  const [resume, setResume] = useState(undefined);   // undefined=loading, null=none, obj=resume
  const [profile, setProfile] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [jobUrl, setJobUrl] = useState('');
  const [jobCheck, setJobCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [level, setLevel] = useState('student');
  const [activeId, setActiveId] = useState(null);     // the application being watched
  const [dispatching, setDispatching] = useState(false);
  const [answers, setAnswers] = useState({});         // answer-once drafts being edited
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [histFilter, setHistFilter] = useState('all'); // history view: all | submitted
  const [detailId, setDetailId] = useState(null);      // application open in the detail modal
  const [detail, setDetail] = useState(null);          // fetched detail payload
  const [detailLoading, setDetailLoading] = useState(false);
  const [view, setView] = useState('overview');        // which section-view is showing
  const [refreshing, setRefreshing] = useState(false); // a feed-refresh crawl is running
  const [feedTotal, setFeedTotal] = useState(null);    // live postings in the whole feed
  const [featureOn, setFeatureOn] = useState(null);    // null = checking; false = Coming soon page
  const [uploadingResume, setUploadingResume] = useState(false); // an uploaded resume is being parsed
  const [helper, setHelper] = useState(null);          // {devices, helper_required} from the backend
  const [pairCode, setPairCode] = useState(null);      // {code, expires_in} while pairing this computer
  const [pairing, setPairing] = useState(false);

  // Is Auto-Apply open for business here? In production it launches dark, and
  // the whole page presents as Coming Soon until the switch flips.
  useEffect(() => {
    let alive = true;
    fetch(`${api}payment-config/`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => { if (alive) setFeatureOn(cfg ? cfg.autoapply_enabled !== false : true); })
      .catch(() => { if (alive) setFeatureOn(true); });
    return () => { alive = false; };
  }, []);

  const template = localStorage.getItem('resumeTemplate') || 'modern';
  const Tmpl = TEMPLATES[template] || ModernTemplate;

  useEffect(() => {
    if (!loggedIn) return;
    axios.get(`${api}autoapply/balance/`, { withCredentials: true })
      .then((r) => setBalance(r.data.balance)).catch(() => setBalance(0));
    axios.get(`${api}autoapply/resume/`, { withCredentials: true })
      .then((r) => setResume(r.data.resume || null)).catch(() => setResume(null));
    axios.get(`${api}autoapply/profile/`, { withCredentials: true })
      .then((r) => setProfile(r.data.profile || {})).catch(() => setProfile({}));
    axios.get(`${api}autoapply/applications/`, { withCredentials: true })
      .then((r) => setApplications(r.data.applications || [])).catch(() => setApplications([]));
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    setJobsLoading(true);
    axios.get(`${api}autoapply/jobs/?limit=30&level=${level}`, { withCredentials: true })
      .then((r) => { setJobs(r.data.jobs || []); setFeedTotal(r.data.total ?? null); })
      .catch(() => setJobs([]))
      .finally(() => setJobsLoading(false));
  }, [loggedIn, level]);

  // While a feed-refresh crawl runs, re-pull the list every 10s so new postings
  // stream in as they land; stop after ~90s (the crawl is done well before that).
  useEffect(() => {
    if (!refreshing) return;
    let ticks = 0;
    const t = setInterval(() => {
      ticks += 1;
      axios.get(`${api}autoapply/jobs/?limit=30&level=${level}`, { withCredentials: true })
        .then((r) => setJobs(r.data.jobs || [])).catch(() => {});
      if (ticks >= 9) setRefreshing(false);
    }, 10000);
    return () => clearInterval(t);
  }, [refreshing, level]);

  // Upload an existing resume for Auto-Apply to submit, instead of building one
  // here. Parsed once, extraction only: the candidate's own words.
  const uploadResume = async (file) => {
    setUploadingResume(true);
    try {
      const fd = new FormData();
      fd.append('resume_file', file);
      const { data } = await axios.post(`${api}autoapply/resume/upload/`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResume(data.resume);
      toast.success('Resume uploaded. Auto-Apply will submit this one from now on.');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not read that file. PDF, DOCX, or TXT work best.');
    } finally {
      setUploadingResume(false);
    }
  };

  // The Refresh button: asks the backend to re-crawl the shared feed. The backend
  // absorbs button-mashing (15-min cooldown + single-flight), so this is safe to
  // expose to everyone.
  const refreshFeed = async () => {
    if (refreshing) return;
    try {
      const { data } = await axios.post(`${api}autoapply/jobs/ingest/`, {}, { withCredentials: true });
      if (data.fresh) { toast.info(data.message || 'The feed is already fresh.'); return; }
      toast.success('Refreshing the job feed, new postings appear within a minute or two.');
      setRefreshing(true);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not refresh the feed.');
    }
  };

  const refreshApplications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${api}autoapply/applications/`, { withCredentials: true });
      setApplications(data.applications || []);
      return data.applications || [];
    } catch { return []; }
  }, []);

  // The Helper is the production data plane: assist runs happen in a Chrome on
  // YOUR computer, driven by the paired Resume Advisory Helper. This tracks
  // whether one is paired and running, and gates Apply honestly when it isn't.
  const refreshHelper = useCallback(async () => {
    try {
      const { data } = await axios.get(`${api}autoapply/helper/devices/`, { withCredentials: true });
      setHelper(data);
      return data;
    } catch { return null; }
  }, []);
  useEffect(() => { if (loggedIn) refreshHelper(); }, [loggedIn, refreshHelper]);
  // While a pairing code is on screen, watch for the helper to come online.
  useEffect(() => {
    if (!pairCode) return undefined;
    const t = setInterval(async () => {
      const h = await refreshHelper();
      if ((h?.devices || []).some((d) => d.online)) {
        setPairCode(null);
        toast.success('Helper connected. This computer can now run your applications.');
      }
    }, 4000);
    return () => clearInterval(t);
  }, [pairCode, refreshHelper]);

  const startPairing = async () => {
    setPairing(true);
    try {
      const { data } = await axios.post(`${api}autoapply/helper/pair/start/`, {}, { withCredentials: true });
      setPairCode(data);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not create a pairing code. Try again.');
    }
    setPairing(false);
  };

  const revokeDevice = async (id) => {
    try {
      await axios.post(`${api}autoapply/helper/devices/`, { action: 'revoke', device_id: id }, { withCredentials: true });
      await refreshHelper();
      toast.info('Disconnected. That computer can no longer run your applications.');
    } catch { toast.error('Could not disconnect that computer.'); }
  };

  const removeAnswer = async (q) => {
    try {
      const { data } = await axios.post(`${api}autoapply/profile/`, { remove_answers: [q] }, { withCredentials: true });
      setProfile(data.profile);
    } catch { toast.error('Could not remove that answer.'); }
  };

  // Fetch the full frozen record when an applied row is opened in the detail modal.
  useEffect(() => {
    if (!loggedIn || detailId == null) { setDetail(null); return; }
    let alive = true;
    let timer = null;
    const fetchDetail = (first) => {
      if (first) { setDetailLoading(true); setDetail(null); }
      axios.get(`${api}autoapply/applications/${detailId}/`, { withCredentials: true })
        .then((r) => {
          if (!alive) return;
          setDetail(r.data);
          // An open modal on an in-flight run must live-update, a frozen
          // snapshot showed "Nothing recorded yet" forever while the run
          // settled behind it.
          if (['queued', 'held'].includes(r.data.status)) timer = setTimeout(() => fetchDetail(false), 3000);
        })
        .catch(() => { if (alive && first) setDetail({ error: true }); })
        .finally(() => { if (alive && first) setDetailLoading(false); });
    };
    fetchDetail(true);
    return () => { alive = false; if (timer) clearTimeout(timer); };
  }, [loggedIn, detailId]);

  // While a run is in flight, poll for its result; refresh the balance once it settles.
  useEffect(() => {
    if (!loggedIn) return;
    if (!applications.some((a) => a.status === 'queued' || a.status === 'held')) return;
    const t = setInterval(async () => {
      const apps = await refreshApplications();
      if (!apps.some((a) => a.status === 'queued' || a.status === 'held')) {
        axios.get(`${api}autoapply/balance/`, { withCredentials: true })
          .then((r) => setBalance(r.data.balance)).catch(() => {});
      }
    }, 2500);   // stages transition within seconds, poll often enough to show them
    return () => clearInterval(t);
  }, [loggedIn, applications, refreshApplications]);

  // The Apply click. mode 'fill' is the dry run (fills the real form, sends
  // nothing, charges nothing); 'assist' opens a visible Chrome window, fills it,
  // and waits for YOU to press Submit; 'submit' is the fully autonomous send.
  // Both send modes stay server-gated.
  const dispatch = useCallback(async (url, mode = 'fill', match = null) => {
    setDispatching(true);
    try {
      const body = ['submit', 'assist'].includes(mode) ? { url, mode, confirm: true } : { url, mode };
      if (match != null) body.match = match;
      const { data } = await axios.post(`${api}autoapply/dispatch/`, body, { withCredentials: true });
      setActiveId(data.application_id);
      setAnswers({});
      // Stay on the current view so you can keep applying to more jobs, the run
      // proceeds in the background; watch it under "Applications" whenever you like.
      // Applied → it leaves "Matched for you" immediately (optimistic, matching the
      // server-side exclusion) so you can't apply to the same posting twice.
      setJobs((js) => js.filter((j) => j.url !== url && j.job_id !== data.job_id));
      // The backend tells us what it actually did: an assisted send, a real
      // submit, or a dry run (when live sending is off).
      toast.success(data.mode === 'assist'
        ? 'A Chrome window is opening. It fills the whole form for you, so review it there and press Submit.'
        : data.mode === 'submit'
        ? 'A Chrome window is opening. Automation is on: it fills everything and presses Submit itself; if anything needs you (like a CAPTCHA), the window waits for you.'
        : 'Applying, filling the employer\'s form. (Live sending is off, so nothing is sent yet.)');
      await refreshApplications();
    } catch (e) {
      const d = e?.response?.data || {};
      if (d.error === 'sends_disabled') toast.info('Live sending isn\'t enabled yet, dry runs only for now.');
      else if (d.error === 'already_submitted') toast.info('You already applied to this posting.');
      else if (d.error === 'no_credits') toast.error('You\'re out of credits, buy a pack first.');
      else if (d.error === 'helper_offline') { toast.error(d.message || 'Start the Resume Advisory Helper on your computer first.'); setView('apply'); refreshHelper(); }
      else toast.error(d.message || 'Could not start this application.');
    }
    setDispatching(false);
  }, [refreshApplications, refreshHelper]);

  // Save the user's answers/approvals as learned profile answers, so every later
  // form that asks the same question is covered and the board runs unattended.
  const saveAnswers = useCallback(async (app, redispatch = true) => {
    // Approving an agent draft without editing it still counts as answering it:
    // seed from each review-flagged draft's value, then let any user edits win.
    const drafts = ((app?.preview || {}).agent_answers || []).filter((d) => d.review);
    const base = {};
    drafts.forEach((d) => { base[d.question] = d.value; });
    const filled = Object.fromEntries(
      Object.entries({ ...base, ...answers }).filter(([, v]) => String(v || '').trim())
    );
    if (!Object.keys(filled).length) { toast.info('Nothing to save yet, answer a question first.'); return; }
    setSavingAnswers(true);
    try {
      const { data } = await axios.post(`${api}autoapply/profile/`, { application_answers: filled }, { withCredentials: true });
      setProfile(data.profile);
      toast.success('Answers saved, they\'ll be used on every application from now on.');
      // Re-run with the new answers (you press Submit), unless the caller only
      // wanted the answers kept, or no Helper is around to run it right now.
      if (redispatch && app?.posting_url) await dispatch(app.posting_url, profile.auto_submit ? 'submit' : 'assist');
    } catch { toast.error('Could not save your answers.'); }
    setSavingAnswers(false);
  }, [answers, dispatch, profile.auto_submit]);

  const saveProfile = async () => {
    // A malformed phone rides silently onto every application, catch it here,
    // the one moment the user is actually looking at it.
    const digits = String(profile.phone || '').replace(/\D/g, '');
    if (digits && (digits.length < 10 || digits.length > 15)) {
      toast.error('That phone number doesn\'t look complete, employers will use it to call you. Please double-check it.');
      return;
    }
    setSavingProfile(true);
    try {
      const { data } = await axios.post(`${api}autoapply/profile/`, profile, { withCredentials: true });
      setProfile(data.profile);
      toast.success('Profile saved.');
    } catch { toast.error('Could not save your profile.'); }
    setSavingProfile(false);
  };

  const checkJob = async () => {
    setChecking(true); setJobCheck(null);
    try {
      const { data } = await axios.post(`${api}autoapply/jobs/validate/`, { url: jobUrl }, { withCredentials: true });
      setJobCheck(data);
    } catch (e) {
      setJobCheck(e?.response?.data || { ok: false, message: 'Could not validate this posting.' });
    }
    setChecking(false);
  };

  // The application currently on screen: the one just dispatched, else the most
  // recent one that still needs the user (a preview or a stopped submit). Derived
  // before any early return so the hook below always runs in the same order.
  const activeApp = applications.find((a) => a.id === activeId)
    || applications.find((a) => ['queued', 'held', 'filled'].includes(a.status) || a.reason === 'needs_review' || answerableFail(a));

  // In-progress answers belong to ONE application. Clear them whenever the shown
  // application changes, so a draft typed for one posting can never be saved
  // against another after switching via the history list.
  const shownId = activeApp?.id;
  useEffect(() => { setAnswers({}); }, [shownId]);

  // Hold rendering for the instant it takes to learn the launch state, so
  // production visitors never see the dashboard flash before Coming Soon.
  if (featureOn === null) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white" />
      </>
    );
  }

  // Production launch state: the feature is not open yet, so the page is a
  // proper Coming Soon, not a dashboard full of locked doors.
  if (featureOn === false) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white px-6 py-16">
          <div className="max-w-2xl mx-auto text-center mt-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-100/80 border border-blue-200 px-3 py-1 rounded-full">
              <Zap className="w-3 h-3" /> Auto-Apply
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
              Coming soon!
            </h1>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">
              One click per job: a window opens, the whole application fills itself
              from the resume you already built, and you press Submit. A tracker
              records every confirmation from the employer's own system.
            </p>
            <div className="mt-8 bg-white border border-gray-200 rounded-2xl p-5 text-left space-y-2.5">
              {["A live feed of US jobs, ranked against your resume",
                "Screening questions answered from your real experience, never invented",
                "You always press Submit; nothing is ever sent behind your back",
                "One credit per sent application, and failures refund themselves"].map((line) => (
                <p key={line} className="flex gap-2.5 text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" /> {line}
                </p>
              ))}
            </div>
            <p className="mt-5 text-sm text-gray-500">
              {loggedIn
                ? 'Your account already holds 5 free credits for launch day.'
                : 'Sign up now and your account starts with 5 free credits for launch day.'}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => navigate(loggedIn ? '/upload' : '/signup')}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 shadow-lg shadow-blue-600/20 transition"
              >
                {loggedIn ? 'Build your resume today' : 'Sign up and get ready'}
              </button>
              <button
                onClick={() => navigate('/features')}
                className="px-6 py-3 bg-white text-gray-800 rounded-xl font-semibold border border-gray-300 hover:border-blue-400 hover:text-blue-700 transition"
              >
                See how it works
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!loggedIn) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white px-4 py-10">
          <div className="max-w-md mx-auto text-center mt-20">
            <Zap className="w-10 h-10 text-blue-600 mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Auto-Apply</h1>
            <p className="text-gray-500 mb-6">Sign in to buy credits and let us apply to jobs for you with the resume you already built.</p>
            <button onClick={() => navigate('/signin')} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-500 transition">
              Sign in to continue
            </button>
          </div>
        </div>
      </>
    );
  }

  const missing = profileMissing(profile);
  const resumeName = resume?.contact?.name || '';
  const resumeReady = !!resume && resumeName.trim().split(/\s+/).length >= 2;
  const hasCredits = (balance || 0) >= 1;
  // With a co-located engine (dev) the helper is not required; in production it
  // is, and Apply stays honest about it instead of failing after the click.
  const helperRequired = helper?.helper_required === true;
  const helperOnline = (helper?.devices || []).some((d) => d.online);
  const helperOk = !helperRequired || helperOnline;
  // The user's automation choice: with the opt-in ON, Apply asks for the
  // autonomous press (the server re-checks the consent; the engine hands the
  // window to the human the moment anything needs them).
  const sendMode = profile.auto_submit ? 'submit' : 'assist';
  const readyToApply = resumeReady && missing.length === 0 && hasCredits && jobCheck?.ok && helperOk;

  // The Apply click: an ASSISTED send. A Chrome window opens on this computer,
  // the whole form fills itself autonomously (profile + agent + tailored résumé),
  // and the user presses the employer's own Submit button. A real person sends
  // every application, no bot check is beaten, because no bot presses anything.
  const applyToJob = (job) => {
    if (!resumeReady) { toast.error('Generate a resume with your full name first.'); return; }
    if (missing.length > 0) { toast.error('Complete your profile first so we can fill everything for you.'); setView('profile'); return; }
    if (!hasCredits) { toast.error('You need at least one credit, buy a pack first.'); return; }
    dispatch(job.url, profile.auto_submit ? 'submit' : 'assist', job.match);
  };

  const LEVELS = [['student', 'Student roles'], ['internship', 'Internships'], ['new_grad', 'New grad'], ['entry', 'Entry'], ['', 'All']];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white px-4 py-10">
        <div className="max-w-[1600px] mx-auto">

          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Zap className="w-6 h-6 text-blue-600" /> Auto-Apply
              </h1>
              <p className="text-gray-500 text-sm mt-0.5">
                One click per job: a Chrome window opens, the whole form fills itself with the resume you already generated, and you press Submit. Answer everything once; it never stops to ask.
              </p>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-400">Credits</div>
              <div className="text-2xl font-extrabold text-gray-900">{balance == null ? '…' : balance}</div>
            </div>
          </div>

          {/* View switcher, each section is its own view (click to switch) */}
          <div className="sticky top-0 z-30 -mx-4 px-4 py-2 mb-5 bg-sky-50/90 backdrop-blur border-b border-gray-200">
            <div className="max-w-[1600px] mx-auto flex flex-wrap gap-2">
              {[['overview', 'Overview'], ['jobs', 'Jobs'],
                ['applications', `Applications${applications.length ? ` (${applications.length})` : ''}`],
                ['apply', 'Apply to a job'], ['profile', 'Profile'], ['credits', 'Credits']].map(([id, lbl]) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  className={`text-sm font-medium px-3.5 py-1.5 rounded-full border transition ${
                    view === id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                >{lbl}</button>
              ))}
            </div>
          </div>

          {/* Overview, the home dashboard: every figure at a glance */}
          {view === 'overview' && (() => {
            const submitted = applications.filter((a) => ['pressed', 'confirmed'].includes(a.status));
            const confirmedApps = applications.filter((a) => a.status === 'confirmed');
            const inflightApps = applications.filter((a) => ['queued', 'held'].includes(a.status));
            const needsApps = applications.filter((a) => appBucket(a) === 'needs');
            const fieldsFilled = applications.reduce((s, a) => s + (a.filled_count || 0), 0);
            const thisWeek = applications.filter((a) => Date.now() - new Date(a.created_at).getTime() < 7 * 864e5).length;
            const topMatch = jobs[0];
            return (
              <>
                {/* Hero strip, the hook */}
                <div className="mb-5 rounded-2xl bg-gradient-to-r from-blue-700 via-blue-600 to-sky-500 text-white p-6 shadow-lg">
                  <div className="flex flex-wrap items-end justify-between gap-4">
                    <div className="min-w-0">
                      <p className="text-blue-100 text-sm font-medium">Your job search, running itself</p>
                      <h2 className="text-3xl font-extrabold mt-1 leading-tight">
                        {submitted.length} application{submitted.length === 1 ? '' : 's'} submitted
                        {confirmedApps.length > 0 && (
                          <span className="block sm:inline text-lg font-semibold text-blue-100 sm:ml-3">
                            {confirmedApps.length} confirmed received by employers
                          </span>
                        )}
                      </h2>
                      <p className="text-blue-100 text-sm mt-2">
                        {jobs.length > 0
                          ? <>Top {jobs.length} matches ready{feedTotal ? ` from ${feedTotal.toLocaleString()} live postings` : ''}{topMatch?.match != null ? <>, best fit right now: <span className="font-semibold text-white">{topMatch.company} · {topMatch.match}%</span></> : ''}</>
                          : 'Your matched-jobs feed is loading…'}
                      </p>
                    </div>
                    <button
                      onClick={() => setView('jobs')}
                      className="bg-white text-blue-700 font-bold px-5 py-2.5 rounded-xl hover:bg-blue-50 transition flex items-center gap-2 flex-shrink-0"
                    >
                      <Zap className="w-4 h-4" /> Apply to matches
                    </button>
                  </div>
                </div>

                {/* The figures */}
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
                  <StatTile label="Submitted" value={submitted.length} sub="Sent to real employers" accent="text-green-600" />
                  <StatTile label="Confirmed" value={confirmedApps.length} sub="Employer acknowledged receipt" accent="text-green-700" />
                  <StatTile label="In flight" value={inflightApps.length} sub="Being filled right now" accent={inflightApps.length ? 'text-blue-600' : 'text-gray-900'} />
                  <StatTile label="Needs you" value={needsApps.length} sub="One answer from done" accent={needsApps.length ? 'text-amber-600' : 'text-gray-900'} />
                  <StatTile label="Fields auto-filled" value={fieldsFilled} sub="Typed for you by the agent" accent="text-blue-600" />
                  <StatTile label="Credits" value={balance == null ? '…' : balance} sub="1 credit = 1 sent application" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                  {/* Activity calendar */}
                  <div className={CARD}>
                    <div className="flex items-baseline justify-between mb-4">
                      <h3 className="font-bold text-gray-800">Application activity</h3>
                      <span className="text-xs text-gray-400">{thisWeek} in the last 7 days</span>
                    </div>
                    <div className="overflow-x-auto pb-1">
                      <ActivityCalendar applications={applications} />
                    </div>
                  </div>

                  {/* Pipeline + recent */}
                  <div className={CARD}>
                    <h3 className="font-bold text-gray-800 mb-3">Pipeline</h3>
                    <PipelineBar applications={applications} />
                    <div className="flex items-baseline justify-between mt-5 mb-1">
                      <h4 className="font-semibold text-gray-700 text-sm">Recent</h4>
                      {applications.length > 5 && (
                        <button className="text-xs text-blue-600 hover:underline" onClick={() => setView('applications')}>See all →</button>
                      )}
                    </div>
                    <div className="divide-y divide-gray-50">
                      {applications.length === 0 && (
                        <p className="text-sm text-gray-400 py-3">Nothing yet, your first application is one click away.</p>
                      )}
                      {applications.slice(0, 5).map((a) => {
                        const [dot, txt, blabel] = BUCKET_UI[appBucket(a)] || ['bg-gray-400', 'text-gray-500', a.status];
                        return (
                          <button key={a.id} onClick={() => setDetailId(a.id)}
                                  className="w-full flex items-center gap-3 py-2 text-left hover:bg-gray-50 rounded-lg px-1 transition">
                            <span className="w-7 h-7 rounded-lg bg-blue-50 text-blue-700 font-bold text-xs flex items-center justify-center flex-shrink-0">
                              {(a.company || '?').trim().charAt(0).toUpperCase()}
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-medium text-gray-800 truncate">{a.company || a.posting_url}</span>
                              <span className="block text-[11px] text-gray-400 truncate">{a.role}</span>
                            </span>
                            <span className={`flex items-center gap-1.5 text-xs ${txt} flex-shrink-0`}>
                              <span className={`w-2 h-2 rounded-full ${dot}`} />
                              {a.status === 'pressed' ? 'Sent' : blabel}
                            </span>
                            <span className="text-[11px] text-gray-400 flex-shrink-0 w-12 text-right">{fmtDate(a.created_at)}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Matched jobs, the hero: ranked live feed against your resume */}
          <div id="jobs" className={view === 'jobs' ? `${CARD} mb-5` : 'hidden'}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-600" /> Matched for you
                {jobs.length > 0 && <span className="text-sm font-normal text-gray-400">ranked against your resume</span>}
                <button
                  onClick={refreshFeed}
                  disabled={refreshing}
                  title="Re-crawl the employer job boards for new postings"
                  className="text-xs font-medium px-2.5 py-1 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60 transition flex items-center gap-1.5"
                >
                  <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
                  {refreshing ? 'Refreshing…' : 'Refresh'}
                </button>
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {LEVELS.map(([val, lbl]) => (
                  <button
                    key={lbl}
                    onClick={() => setLevel(val)}
                    className={`text-xs font-medium px-2.5 py-1 rounded-full border transition ${
                      level === val ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                  >{lbl}</button>
                ))}
              </div>
            </div>

            {missing.length > 0 && (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                <span className="text-sm text-amber-800">
                  Complete your <span className="font-semibold">profile</span> first, {missing.length} field{missing.length > 1 ? 's' : ''} left. Then Auto-Apply runs each application autonomously, without stopping to ask.
                </span>
                <button onClick={() => setView('profile')} className="text-sm font-semibold bg-amber-600 text-white px-3 py-1.5 rounded-lg hover:bg-amber-500 transition">
                  Complete profile →
                </button>
              </div>
            )}

            {jobsLoading ? (
              <div className="py-10 text-center"><ClipLoader color="#b08d57" size={34} /></div>
            ) : jobs.length === 0 ? (
              <p className="text-gray-400 text-sm py-6 text-center">No matching jobs right now, try a wider filter.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
                {jobs.map((j) => (
                  <div key={j.job_id} className="border border-gray-200 rounded-xl p-3 flex flex-col hover:border-blue-300 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{j.title}</p>
                        <p className="text-sm text-gray-500 truncate">{j.company}</p>
                      </div>
                      {j.match != null && (
                        <div className="flex-shrink-0 text-center">
                          <div className={`text-lg font-extrabold leading-none ${j.match >= 70 ? 'text-green-600' : j.match >= 60 ? 'text-amber-600' : 'text-gray-400'}`}>{j.match}%</div>
                          <div className="text-[10px] text-gray-400">match</div>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                      {j.level && <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{j.level.replace('_', ' ')}</span>}
                      {j.location && <span className="text-gray-400 flex items-center gap-0.5"><MapPin className="w-3 h-3" />{j.location}</span>}
                      {j.sponsorship === 'offered' && <span className="text-green-600">sponsors visas</span>}
                      {j.sponsorship === 'refused' && <span className="text-red-500">no sponsorship</span>}
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <a href={j.url} target="_blank" rel="noreferrer" className="text-xs text-gray-500 hover:text-gray-700 py-1.5">View posting ↗</a>
                      <button
                        onClick={() => applyToJob(j)}
                        disabled={dispatching || missing.length > 0}
                        title={missing.length > 0 ? 'Complete your profile first' : ''}
                        className="ml-auto text-xs font-semibold bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center gap-1"
                      >
                        <Zap className="w-3 h-3" /> Apply
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* All applications, the tracker: applied jobs live here, click to open detail */}
          {view === 'applications' && applications.length === 0 && (
            <div className={`${CARD} mb-5 text-center text-gray-400 py-10`}>
              You haven't applied to anything yet. Go to <button className="text-blue-600 underline" onClick={() => setView('jobs')}>Jobs</button> and click Apply.
            </div>
          )}
          {view === 'applications' && applications.length > 0 && (() => {
            const counts = {
              all: applications.length,
              submitted: applications.filter((a) => appBucket(a) === 'submitted').length,
              ready: applications.filter((a) => appBucket(a) === 'ready').length,
              inflight: applications.filter((a) => appBucket(a) === 'inflight').length,
              needs: applications.filter((a) => appBucket(a) === 'needs').length,
              failed: applications.filter((a) => appBucket(a) === 'failed').length,
            };
            const rows = histFilter === 'all' ? applications : applications.filter((a) => appBucket(a) === histFilter);
            const PILLS = [['all', 'All'], ['submitted', 'Submitted'], ['ready', 'Ready'], ['needs', 'Needs you'], ['inflight', 'In flight'], ['failed', 'Failed']];
            return (
              <div id="applications" className={`${CARD} mb-5 scroll-mt-24`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <h3 className="font-bold text-gray-800 text-lg">All applications</h3>
                  <span className="text-sm text-gray-500"><span className="font-bold text-green-700">{counts.submitted}</span> submitted</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {PILLS.map(([val, lbl]) => (
                    <button
                      key={val}
                      onClick={() => setHistFilter(val)}
                      className={`text-xs font-medium px-3 py-1 rounded-full border transition ${
                        histFilter === val ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                    >{lbl} <span className={histFilter === val ? 'text-blue-100' : 'text-gray-400'}>{counts[val]}</span></button>
                  ))}
                </div>
                {/* header row */}
                <div className="hidden sm:grid grid-cols-12 gap-2 px-3 pb-2 text-[11px] font-semibold text-gray-400 uppercase tracking-wide border-b border-gray-100">
                  <div className="col-span-6">Company</div>
                  <div className="col-span-2">Fit</div>
                  <div className="col-span-3">Status</div>
                  <div className="col-span-1 text-right">Applied</div>
                </div>
                <div className="divide-y divide-gray-100">
                  {rows.length === 0 ? (
                    <p className="text-sm text-gray-400 py-6 text-center">Nothing here.</p>
                  ) : rows.map((a) => {
                    const [dot, txt, blabel] = BUCKET_UI[appBucket(a)] || ['bg-gray-400', 'text-gray-500', a.status];
                    const label = a.status === 'pressed' ? 'Sent, awaiting confirmation' : blabel;
                    return (
                      <button
                        key={a.id}
                        onClick={() => setDetailId(a.id)}
                        className="w-full grid grid-cols-12 gap-2 items-center px-3 py-3 hover:bg-gray-50 transition text-left"
                      >
                        <div className="col-span-6 flex items-center gap-3 min-w-0">
                          <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-700 font-bold text-sm flex items-center justify-center flex-shrink-0">
                            {(a.company || '?').trim().charAt(0).toUpperCase()}
                          </span>
                          <span className="min-w-0">
                            <span className="block font-medium text-gray-800 truncate">{a.company || a.posting_url}</span>
                            {a.role ? <span className="block text-xs text-gray-400 truncate">{a.role}</span> : null}
                          </span>
                        </div>
                        <div className="col-span-2">
                          {a.interview_score != null
                            ? <span className={`text-sm font-bold ${a.interview_score >= 70 ? 'text-green-600' : a.interview_score >= 55 ? 'text-amber-600' : 'text-gray-500'}`}>{a.interview_score}%</span>
                            : <span className="text-gray-300">-</span>}
                        </div>
                        <div className="col-span-3 min-w-0">
                          <div className={`flex items-center gap-1.5 text-sm ${txt}`}>
                            <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`} />
                            <span className="truncate">{label}</span>
                          </div>
                          {appBucket(a) === 'failed' && a.reason && (
                            <p className="text-[11px] text-gray-400 truncate mt-0.5">{explainReason(a.reason)}</p>
                          )}
                          {answerableFail(a) && (
                            <p className="text-[11px] text-amber-600 truncate mt-0.5">Answer: {openQuestions(a)[0]}</p>
                          )}
                        </div>
                        <div className="col-span-1 text-right text-xs text-gray-400">{fmtDate(a.created_at)}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div className="flex flex-col gap-5 max-w-3xl mx-auto">

            {/* Left column */}
            <div className="flex flex-col gap-5 empty:hidden">
              {/* 1. Credits */}
              <div id="credits" className={view === 'credits' ? CARD : 'hidden'}>
                <Elements stripe={stripePromise}>
                  <CreditPurchase balance={balance} onPurchased={(b) => setBalance(b)} />
                </Elements>
              </div>

              {/* 2. Application profile */}
              <div id="profile" className={view === 'profile' ? CARD : 'hidden'}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-gray-800 text-lg">Application profile</h3>
                  {missing.length === 0
                    ? <span className="text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">Complete</span>
                    : <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">{missing.length} to answer</span>}
                </div>
                <p className="text-gray-500 text-sm mb-4">Answered once, reused on every application, so a run never pauses to ask.</p>

                {/* Autonomous mode, the opt-in for zero per-application review */}
                <label className="flex items-start gap-3 mb-4 p-3 rounded-xl bg-blue-50 border border-blue-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!profile.autonomous_mode}
                    onChange={(e) => setProfile((p) => ({ ...p, autonomous_mode: e.target.checked }))}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">Fully autonomous</span>, let Auto-Apply answer open-ended questions and use its wording (drawn from your “About you” below) <span className="font-semibold">without stopping for your review</span>. It still never invents facts, never volunteers protected-class info, and never sends until you’ve enabled live sending.
                  </span>
                </label>

                {/* Automation: the user's explicit opt-in for the agent to press
                    Submit itself. Enforced server-side too; the engine hands the
                    window to the human the moment anything needs them, and it
                    never solves or evades a verification challenge. */}
                <label className="flex items-start gap-3 mb-4 p-3 rounded-xl bg-green-50 border border-green-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!profile.auto_submit}
                    onChange={(e) => setProfile((p) => ({ ...p, auto_submit: e.target.checked }))}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-700">
                    <span className="font-semibold">Automation: press Submit for me.</span> When the form is completely filled and nothing needs you, the agent presses Submit itself, right in the visible window. The moment something only you can do appears (a verification challenge, a question it can't answer truthfully), the window simply waits for you instead of sending anything. Heads up: some employer sites treat automated submissions as spam; if that happens we tell you honestly and refund the credit.
                  </span>
                </label>

                <div className="space-y-3">
                  {PROFILE_FIELDS.map((f) => (
                    <div key={f.key}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {f.label}{f.required && <span className="text-red-500"> *</span>}
                      </label>
                      {f.type === 'bool' ? (
                        <div className="flex gap-2">
                          {[['Yes', true], ['No', false]].map(([lbl, val]) => (
                            <button
                              key={lbl} type="button"
                              onClick={() => setProfile((p) => ({ ...p, [f.key]: val }))}
                              className={`flex-1 py-2 rounded-lg text-sm font-medium border transition ${
                                profile[f.key] === val
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                            >{lbl}</button>
                          ))}
                        </div>
                      ) : f.type === 'textarea' ? (
                        <textarea
                          rows={4}
                          value={profile[f.key] || ''}
                          placeholder={f.placeholder}
                          onChange={(e) => setProfile((p) => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      ) : (
                        <input
                          type="text"
                          value={profile[f.key] || ''}
                          placeholder={f.placeholder}
                          onChange={(e) => setProfile((p) => ({ ...p, [f.key]: e.target.value }))}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                      )}
                    </div>
                  ))}

                  {/* Answers learned from real applications, including what you
                      typed straight into the assist window. Editable, one bad
                      answer must never silently ride onto every future form. */}
                  {Object.keys(profile.application_answers || {}).length > 0 && (
                    <div className="pt-3 border-t border-gray-100">
                      <h4 className="font-semibold text-gray-800 text-sm mb-1">Answers we've learned</h4>
                      <p className="text-xs text-gray-400 mb-2">
                        Saved from your applications, including anything you typed into the Chrome window before pressing Submit. Each one is reused whenever a form asks the same question. Remove any you don't want kept.
                      </p>
                      <div className="space-y-2 max-h-64 overflow-auto pr-1">
                        {Object.entries(profile.application_answers).map(([q, a]) => (
                          <div key={q} className="flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-700 truncate" title={q}>{q}</p>
                              <p className="text-xs text-gray-500 break-words">{String(a)}</p>
                            </div>
                            <button
                              onClick={() => removeAnswer(q)}
                              title="Forget this answer"
                              className="text-gray-300 hover:text-red-500 text-base font-bold px-1 leading-none"
                            >×</button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* EEO consent, off by default */}
                  <label className="flex items-start gap-2 pt-2 border-t border-gray-100">
                    <input
                      type="checkbox"
                      checked={!!profile.demographic_auto_answer}
                      onChange={(e) => setProfile((p) => ({ ...p, demographic_auto_answer: e.target.checked }))}
                      className="mt-1"
                    />
                    <span className="text-xs text-gray-500">
                      Let Auto-Apply answer optional EEO / demographic questions with "prefer not to say". Off by default, these are always yours to answer, and we never volunteer a protected-class value.
                    </span>
                  </label>

                  {/* Policy acknowledgements, off by default */}
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={!!profile.acknowledge_policies}
                      onChange={(e) => setProfile((p) => ({ ...p, acknowledge_policies: e.target.checked }))}
                      className="mt-1"
                    />
                    <span className="text-xs text-gray-500">
                      Let Auto-Apply tick required "I have read the privacy notice / terms" checkboxes for me. Off by default, without this, any form with one pauses for you.
                    </span>
                  </label>
                </div>

                <button
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="mt-4 w-full py-2.5 rounded-xl bg-gray-800 text-white font-semibold hover:bg-gray-700 disabled:opacity-50 transition"
                >
                  {savingProfile ? 'Saving…' : 'Save profile'}
                </button>
              </div>
            </div>

            {/* Right column */}
            <div className="flex flex-col gap-5">
              {/* 2.5 The Helper: the app on your computer that opens the Chrome
                  window and fills the form. Applications physically happen there,
                  so its connection state leads the Apply view. */}
              {helperRequired && (
                <div className={view === 'apply' ? CARD : 'hidden'}>
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-gray-800 text-lg">Your computer's Helper</h3>
                    {helperOnline
                      ? <span className="flex items-center gap-1.5 text-xs text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"><span className="w-2 h-2 rounded-full bg-green-500" /> Connected</span>
                      : <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full"><span className="w-2 h-2 rounded-full bg-gray-300" /> Not running</span>}
                  </div>
                  <p className="text-gray-500 text-sm mb-3">
                    Applications run on your own computer: the Helper opens a Chrome window here, fills the whole form, and you press Submit. That's what keeps every application honest and every submission yours.
                  </p>

                  {(helper?.devices || []).length > 0 && (
                    <div className="space-y-2 mb-3">
                      {helper.devices.map((d) => (
                        <div key={d.id} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                          <div className="flex items-center gap-2 text-sm text-gray-700">
                            <span className={`w-2 h-2 rounded-full ${d.online ? 'bg-green-500' : 'bg-gray-300'}`} />
                            <span className="font-medium">{d.name || 'Computer'}</span>
                            <span className="text-xs text-gray-400">{d.online ? 'online now' : (d.last_seen ? 'last seen ' + new Date(d.last_seen).toLocaleString() : 'never seen')}</span>
                          </div>
                          <button onClick={() => revokeDevice(d.id)} className="text-xs text-gray-400 hover:text-red-500 font-semibold">Disconnect</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {pairCode ? (
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-center">
                      <p className="text-sm text-gray-700 mb-2">In the Helper on your computer, enter this code:</p>
                      <p className="text-3xl font-bold tracking-[0.3em] text-blue-700 select-all">{pairCode.code}</p>
                      <p className="text-xs text-gray-400 mt-2">The code works for 10 minutes. This page updates by itself the moment the Helper connects.</p>
                      <p className="text-xs text-gray-400 mt-1">Helper not installed yet? In the helper folder run: <code className="bg-white border border-gray-200 rounded px-1">npm run helper -- pair {pairCode.code}</code></p>
                    </div>
                  ) : (
                    !helperOnline && (
                      <button
                        onClick={startPairing}
                        disabled={pairing}
                        className="w-full py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-500 disabled:opacity-50 transition"
                      >
                        {pairing ? 'Creating a code…' : ((helper?.devices || []).length ? 'Pair another computer' : 'Connect this computer')}
                      </button>
                    )
                  )}
                  {!helperOnline && (helper?.devices || []).length > 0 && !pairCode && (
                    <p className="text-xs text-amber-600 mt-2">A computer is paired but its Helper isn't running. Start it there, or pair this one.</p>
                  )}
                </div>
              )}

              {/* 3. Resume we'll submit: built here, or uploaded directly */}
              <div className={view === 'apply' ? CARD : 'hidden'}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-bold text-gray-800 text-lg">The resume we'll submit</h3>
                  <label className={`text-xs font-semibold cursor-pointer ${uploadingResume ? 'text-gray-400' : 'text-blue-600 hover:text-blue-500'}`}>
                    {uploadingResume ? 'Reading your resume…' : (resume ? 'Replace with an upload' : 'Upload a resume')}
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.txt"
                      className="hidden"
                      disabled={uploadingResume}
                      onChange={(e) => { if (e.target.files?.[0]) uploadResume(e.target.files[0]); e.target.value = ''; }}
                    />
                  </label>
                </div>
                {resume === undefined || uploadingResume ? (
                  <div className="py-10 text-center"><ClipLoader color="#b08d57" size={34} /></div>
                ) : resume === null ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                    Auto-Apply needs a resume. Build one here, or upload the one you already have
                    using the link above.
                    <button onClick={() => navigate('/upload')} className="ml-1 underline font-semibold">Build one →</button>
                  </div>
                ) : (
                  <>
                    {!resumeReady && (
                      <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                        Your resume name looks incomplete (a full first + last name is required by most employer forms).
                      </div>
                    )}
                    <div className="max-h-[420px] overflow-auto border border-gray-100 rounded-xl">
                      <Tmpl resume={resume} />
                    </div>
                  </>
                )}
              </div>

              {/* 4. Pick a job + apply */}
              <div id="apply" className={view === 'apply' ? CARD : 'hidden'}>
                <h3 className="font-bold text-gray-800 text-lg mb-1">Apply to a job</h3>
                <p className="text-gray-500 text-sm mb-3">Paste a Greenhouse, Lever, or Ashby posting link.</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={jobUrl}
                    onChange={(e) => { setJobUrl(e.target.value); setJobCheck(null); }}
                    placeholder="https://boards.greenhouse.io/company/jobs/1234567"
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button
                    onClick={checkJob}
                    disabled={!jobUrl.trim() || checking}
                    className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 disabled:opacity-50 transition"
                  >
                    {checking ? 'Checking…' : 'Check'}
                  </button>
                </div>
                {jobCheck && (
                  <div className={`mt-2 text-sm flex items-center gap-1.5 ${jobCheck.ok ? 'text-green-700' : 'text-red-600'}`}>
                    {jobCheck.ok ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {jobCheck.ok ? `Valid ${jobCheck.ats} posting` : (jobCheck.message || 'Not a supported posting')}
                  </div>
                )}

                {/* Readiness checklist */}
                <div className="mt-4 space-y-1.5 text-sm">
                  {[
                    ['A generated resume with a full name', resumeReady],
                    ['Application profile complete', missing.length === 0],
                    ['At least one credit', hasCredits],
                    ['A valid job posting', !!jobCheck?.ok],
                    ...(helperRequired ? [['The Helper running on this computer', helperOnline]] : []),
                  ].map(([label, ok]) => (
                    <div key={label} className="flex items-center gap-2">
                      {ok ? <CheckCircle className="w-4 h-4 text-green-500" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" />}
                      <span className={ok ? 'text-gray-700' : 'text-gray-400'}>{label}</span>
                    </div>
                  ))}
                  {missing.length > 0 && (
                    <p className="text-xs text-amber-600 pl-6">Still needed: {missing.join(', ')}</p>
                  )}
                </div>

                {/* Apply, one click fills everything in a visible window; you press Submit */}
                <button
                  disabled={!readyToApply || dispatching}
                  className="mt-4 w-full py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
                  onClick={() => dispatch(jobUrl, sendMode)}
                >
                  {dispatching ? <ClipLoader color="#fff" size={16} /> : <><Zap className="w-4 h-4" /> Apply</>}
                </button>
                <p className="text-[11px] text-gray-400 mt-2 text-center">
                  One click opens a Chrome window and fills the whole form, your details, the employer's questions, your tailored résumé. You review it and press Submit yourself. We never solve CAPTCHAs or evade bot checks, and you're only charged when an application is actually sent.
                </p>
              </div>

              {/* 5. The run in progress, dry-run preview, answer-once, then send */}
              {view === 'apply' && activeApp && (() => {
                const pv = activeApp.preview || {};
                const drafts = pv.agent_answers || [];
                const reviewDrafts = drafts.filter((d) => d.review);
                const unansweredQs = pv.unanswered || [];
                const inFlight = activeApp.status === 'queued' || activeApp.status === 'held';
                const readyToSend = activeApp.status === 'filled' && activeApp.ready_to_submit
                  && reviewDrafts.length === 0 && unansweredQs.length === 0;
                const needsUser = unansweredQs.length > 0 || reviewDrafts.length > 0;
                const steps = STAGE_STEPS[activeApp.mode] || STAGE_STEPS.fill;
                const curStage = activeApp.stage || 'preparing';
                const curIdx = Math.max(0, steps.findIndex(([k]) => k === curStage));
                return (
                  <div className={CARD}>
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bold text-gray-800 text-lg">
                        {activeApp.company || 'This application'}{activeApp.role ? `, ${activeApp.role}` : ''}
                      </h3>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        activeApp.status === 'filled' ? 'bg-sky-50 text-sky-700'
                        : inFlight ? 'bg-amber-50 text-amber-700'
                        : activeApp.status === 'confirmed' ? 'bg-green-50 text-green-700'
                        : 'bg-gray-100 text-gray-500'}`}>
                        {inFlight ? (steps[curIdx]?.[1] || 'working…') : activeApp.status === 'filled' ? 'preview ready' : activeApp.status}
                      </span>
                    </div>
                    {pv.learned > 0 && (
                      <p className="text-xs text-green-700 bg-green-50 border border-green-100 rounded-lg px-3 py-2 mb-2 mt-1">
                        Learned {pv.learned} new answer{pv.learned === 1 ? '' : 's'} from what you typed in the window. Next time, those are filled for you.
                      </p>
                    )}

                    {/* Live stage tracker, each step ticks over as the agent works,
                        so you follow progress without ever seeing the browser. */}
                    {inFlight && (
                      <div className="py-3">
                        <ol className="space-y-2">
                          {steps.map(([key, label], i) => {
                            const done = i < curIdx, active = i === curIdx;
                            return (
                              <li key={key} className="flex items-center gap-2.5 text-sm">
                                {done ? (
                                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                ) : active ? (
                                  <ClipLoader color="#2563eb" size={15} />
                                ) : (
                                  <div className="w-4 h-4 rounded-full border-2 border-gray-200 flex-shrink-0" />
                                )}
                                <span className={done ? 'text-gray-500' : active ? 'text-gray-900 font-medium' : 'text-gray-400'}>{label}</span>
                              </li>
                            );
                          })}
                        </ol>
                        <p className="text-[11px] text-gray-400 mt-3">
                          {activeApp.mode === 'assist'
                            ? 'The Chrome window opens on this computer. When it reaches "Your turn", review the filled form there and press Submit yourself.'
                            : `Running in the background${activeApp.mode === 'submit' ? '' : ', nothing is sent in this step'}. You can leave this page; it keeps going.`}
                        </p>
                      </div>
                    )}

                    {!inFlight && pv.message && <p className="text-sm text-gray-600 mb-3">{pv.message}</p>}

                    {!inFlight && (pv.filled || []).length > 0 && (
                      <details className="mb-3">
                        <summary className="text-xs font-semibold text-gray-500 cursor-pointer">
                          {(pv.filled || []).length} fields filled, see what went in
                        </summary>
                        <ul className="mt-2 space-y-1 text-xs text-gray-500 max-h-40 overflow-auto">
                          {(pv.filled || []).map((f) => <li key={f} className="truncate">• {f}</li>)}
                        </ul>
                      </details>
                    )}

                    {/* Agent drafts that need the user's approval before any send */}
                    {!inFlight && reviewDrafts.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-gray-700 mb-2">The agent drafted these, approve or edit them once:</p>
                        <div className="space-y-3">
                          {reviewDrafts.map((d) => (
                            <div key={d.question}>
                              <label className="block text-xs font-medium text-gray-600 mb-1">{d.question}</label>
                              <textarea
                                rows={2}
                                value={answers[d.question] !== undefined ? answers[d.question] : d.value}
                                onChange={(e) => setAnswers((s) => ({ ...s, [d.question]: e.target.value }))}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Questions nothing could answer truthfully, the user's, once */}
                    {!inFlight && unansweredQs.length > 0 && (
                      <div className="mb-3">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Answer these once, every later form that asks the same thing is covered:</p>
                        <div className="space-y-3">
                          {unansweredQs.map((q) => (
                            <div key={q}>
                              <label className="block text-xs font-medium text-gray-600 mb-1">{q}</label>
                              {(pv.choices || {})[q]?.length ? (
                                <select
                                  value={answers[q] || ''}
                                  onChange={(e) => setAnswers((s) => ({ ...s, [q]: e.target.value }))}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
                                >
                                  <option value="">Choose…</option>
                                  {pv.choices[q].map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={answers[q] || ''}
                                  onChange={(e) => setAnswers((s) => ({ ...s, [q]: e.target.value }))}
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {!inFlight && needsUser && (
                      <div className="space-y-2">
                        <button
                          onClick={() => saveAnswers(activeApp)}
                          disabled={savingAnswers || dispatching}
                          className="w-full py-2.5 rounded-xl bg-gray-800 text-white font-semibold hover:bg-gray-700 disabled:opacity-50 transition"
                        >
                          {savingAnswers ? 'Saving…' : 'Save answers & re-check the form'}
                        </button>
                        <button
                          onClick={() => saveAnswers(activeApp, false)}
                          disabled={savingAnswers || dispatching}
                          className="w-full py-2 rounded-xl bg-white border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 disabled:opacity-50 transition"
                        >
                          Just save the answers for later
                        </button>
                      </div>
                    )}

                    {readyToSend && (
                      <>
                        <button
                          onClick={() => dispatch(activeApp.posting_url, sendMode)}
                          disabled={dispatching}
                          className="w-full py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-500 disabled:opacity-50 transition flex items-center justify-center gap-2"
                        >
                          <CheckCircle className="w-4 h-4" /> Everything checks out, open the window &amp; send (1 credit)
                        </button>
                        <p className="text-[11px] text-gray-400 mt-2 text-center">
                          A Chrome window opens with the form filled exactly as above, you press the employer's own Submit button.
                        </p>
                      </>
                    )}

                    {/* Filled, but the form still wants something we could not answer
                        and there is no question to route, always give an action out. */}
                    {!inFlight && !needsUser && !readyToSend && ['filled', 'failed'].includes(activeApp.status) && (
                      <>
                        {(pv.missing_on_form || []).length > 0 && (
                          <p className="text-sm text-amber-700 mb-2">
                            The form still requires: {pv.missing_on_form.join(', ')}. Add these to your profile, then re-check.
                          </p>
                        )}
                        <button
                          onClick={() => dispatch(activeApp.posting_url, sendMode)}
                          disabled={dispatching}
                          className="w-full py-2.5 rounded-xl bg-gray-800 text-white font-semibold hover:bg-gray-700 disabled:opacity-50 transition"
                        >
                          {dispatching ? 'Opening…' : 'Open the window & finish it yourself'}
                        </button>
                      </>
                    )}

                    {!inFlight && pv.agent_unavailable && (
                      <p className="text-[11px] text-amber-600 mt-2">The answer agent was unavailable ({pv.agent_unavailable}), more questions than usual need your answer.</p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Application detail, the frozen record: score, submitted résumé, JD, data fed */}
      {detailId != null && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
             onClick={() => setDetailId(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl my-6 lg:h-[90vh] lg:flex lg:flex-col" onClick={(e) => e.stopPropagation()}>
            {detailLoading || !detail ? (
              <div className="py-16 text-center"><ClipLoader color="#2563eb" size={30} /></div>
            ) : detail.error ? (
              <div className="p-6 text-red-600">Couldn't load this application. <button className="underline" onClick={() => setDetailId(null)}>Close</button></div>
            ) : (
              <div className="p-6 lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
                {/* Header + verdict + score, the at-a-glance strip */}
                <div className="flex items-start justify-between gap-3 mb-4 flex-shrink-0">
                  <div className="flex items-start gap-6 min-w-0 flex-wrap">
                    <div className="min-w-0">
                      <h2 className="text-xl font-bold text-gray-800">{detail.company || 'Application'}</h2>
                      {detail.role && <p className="text-gray-500 truncate">{detail.role}</p>}
                      <a href={detail.posting_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">View job posting ↗</a>
                    </div>
                    {detail.interview_score != null && (
                      <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-100 rounded-xl px-4 py-2">
                        <div className="text-center">
                          <div className={`text-2xl font-extrabold leading-none ${detail.interview_score >= 70 ? 'text-green-600' : detail.interview_score >= 55 ? 'text-amber-600' : 'text-gray-500'}`}>
                            {detail.interview_score}%
                          </div>
                          <div className="text-[10px] text-gray-500 mt-0.5">interview fit</div>
                        </div>
                        <div className="min-w-0 max-w-md hidden sm:block">
                          <p className="text-xs text-gray-600">{(detail.score_basis || {}).summary}</p>
                          {(detail.score_basis?.missing || []).length > 0 && (
                            <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                              <span className="font-semibold text-amber-700">Add to raise it:</span> {detail.score_basis.missing.slice(0, 6).join(', ')}
                            </p>
                          )}
                          {(detail.score_basis?.matched || []).length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {detail.score_basis.matched.slice(0, 8).map((k) => (
                                <span key={k} className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">✓ {k}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <button onClick={() => setDetailId(null)} className="text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0">×</button>
                </div>

                {/* Live progress while the run works, refreshed every 3s */}
                {['queued', 'held'].includes(detail.status) && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4 flex-shrink-0 flex items-center gap-3">
                    <ClipLoader color="#2563eb" size={16} />
                    <p className="text-sm text-blue-800">
                      {({ preparing: 'Tailoring the résumé to this job…', dispatch_unconfirmed: 'Handing off to the automation service…',
                          opening: "Opening the employer's application page…", filling: 'Filling in the application…',
                          reviewing: 'Checking every required field…',
                          awaiting_user: 'It is your turn now. The filled form is open in a Chrome window, so review it and press Submit.',
                          submitting: 'Pressing Submit…',
                          verifying: 'Waiting for the employer to acknowledge…' }[detail.stage])
                        || 'Working on this application…'}
                      {Date.now() - new Date(detail.updated_at).getTime() > 10 * 60 * 1000 && (
                        <span className="text-blue-500"> Taking unusually long, if it stays stuck it will resolve itself within ~30 minutes.</span>
                      )}
                    </p>
                  </div>
                )}

                {/* Why it failed, the exact question this modal exists to answer */}
                {detail.status === 'failed' && !answerableFail(detail) && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 flex-shrink-0">
                    <p className="text-sm text-red-700">
                      <span className="font-semibold">This application didn't go through. </span>
                      {detail.reason ? explainReason(detail.reason) : 'No reason was recorded for the failure.'}
                      {['submit', 'assist'].includes(detail.mode) && <span className="text-red-500"> No charge was made, any held credit was refunded.</span>}
                    </p>
                  </div>
                )}

                {/* One action away: open questions + drafts to approve, then it
                    re-submits itself. Serves EVERY 'needs' row, a failed run
                    with an unanswerable question AND a dry run awaiting drafts. */}
                {appBucket(detail) === 'needs' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4 flex-shrink-0">
                    <p className="text-sm text-amber-800 font-semibold mb-2">
                      {openQuestions(detail).length > 0
                        ? `Everything else is filled, the form needs ${openQuestions(detail).length === 1 ? 'one answer' : `${openQuestions(detail).length} answers`} only you can give.`
                        : 'The agent drafted these answers from your profile, approve them (edit anything first if you like).'}
                      {' '}Nothing has been sent and no credit was used.
                    </p>
                    <div className="space-y-2">
                      {openQuestions(detail).map((q) => (
                        <div key={q}>
                          <p className="text-xs text-gray-700 mb-1">{q}</p>
                          <input
                            type="text"
                            value={answers[q] || ''}
                            onChange={(e) => setAnswers((s) => ({ ...s, [q]: e.target.value }))}
                            placeholder="Your answer (a link is fine)"
                            className="w-full border border-amber-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-amber-400 bg-white"
                          />
                        </div>
                      ))}
                      {reviewDrafts(detail).map((d) => (
                        <div key={d.question}>
                          <p className="text-xs text-gray-700 mb-1">{d.question} <span className="text-gray-400">(drafted by the agent{d.basis ? `, ${d.basis}` : ''})</span></p>
                          <textarea
                            rows={2}
                            value={answers[d.question] ?? d.value}
                            onChange={(e) => setAnswers((s) => ({ ...s, [d.question]: e.target.value }))}
                            className="w-full border border-amber-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-amber-400 bg-white"
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={async () => { await saveAnswers(detail); setDetailId(null); }}
                      disabled={savingAnswers
                        || (reviewDrafts(detail).length === 0
                            && !openQuestions(detail).some((q) => String(answers[q] || '').trim()))}
                      className="mt-3 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-500 disabled:opacity-50 transition"
                    >
                      {savingAnswers ? 'Submitting…'
                        : openQuestions(detail).length > 0 ? 'Save answers & submit application'
                        : 'Approve & submit application'}
                    </button>
                  </div>
                )}

                {/* Everything on one pane: each panel scrolls itself, the page doesn't */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:flex-1 lg:min-h-0">
                  {/* Left: what we sent them */}
                  <div className="flex flex-col gap-4 lg:min-h-0">
                    <section className="border border-gray-100 rounded-xl overflow-hidden flex flex-col lg:flex-1 lg:min-h-0">
                      <h3 className="font-semibold text-gray-800 px-4 py-2.5 bg-gray-50 text-sm flex-shrink-0">
                        What was filled into the application ({(detail.preview?.filled || []).length})
                      </h3>
                      <div className="divide-y divide-gray-50 overflow-auto max-h-64 lg:max-h-none lg:flex-1">
                        {(detail.preview?.filled || []).length === 0
                          ? <p className="text-sm text-gray-400 px-4 py-3">Nothing recorded yet.</p>
                          : (detail.preview.filled || []).map((f) => {
                              const { label, value } = prettyFilled(f);
                              return (
                                <div key={f} className="flex items-baseline gap-3 px-4 py-2 text-sm">
                                  <span className="text-gray-500 w-44 flex-shrink-0 truncate">{label}</span>
                                  <span className="text-gray-800 font-medium truncate">{value || '-'}</span>
                                </div>
                              );
                            })}
                      </div>
                      {(detail.preview?.unanswered || []).length > 0 && (
                        <p className="text-xs text-amber-600 px-4 py-2 border-t border-gray-100 flex-shrink-0">Still needs your answer: {detail.preview.unanswered.join(' · ')}</p>
                      )}
                    </section>

                    <section className="border border-gray-100 rounded-xl overflow-hidden flex flex-col lg:flex-1 lg:min-h-0">
                      <h3 className="font-semibold text-gray-800 px-4 py-2.5 bg-gray-50 text-sm flex-shrink-0">Job description</h3>
                      <div className="overflow-auto px-4 py-3 space-y-3 max-h-64 lg:max-h-none lg:flex-1">
                        {detail.jd_text
                          ? formatJd(detail.jd_text).map(jdParagraph)
                          : <p className="text-sm text-gray-400">No job description was captured.</p>}
                      </div>
                    </section>
                  </div>

                  {/* Right: the résumé that went out, and the page's final state */}
                  <div className="flex flex-col gap-4 lg:min-h-0">
                    <section className="border border-gray-100 rounded-xl overflow-hidden flex flex-col lg:flex-1 lg:min-h-0">
                      <h3 className="font-semibold text-gray-800 px-4 py-2.5 bg-gray-50 text-sm flex-shrink-0">Résumé submitted</h3>
                      <div className="overflow-auto max-h-96 lg:max-h-none lg:flex-1">
                        {detail.resume_snapshot
                          ? <Tmpl resume={detail.resume_snapshot} />
                          : <p className="text-sm text-gray-400 px-4 py-3">The résumé snapshot is still being prepared for this run.</p>}
                      </div>
                    </section>

                    {detail.screenshot_b64 && (
                      <section className="border border-gray-100 rounded-xl overflow-hidden flex flex-col lg:flex-1 lg:min-h-0">
                        <h3 className="font-semibold text-gray-800 px-4 py-2.5 bg-gray-50 text-sm flex-shrink-0">What the employer's page showed</h3>
                        <div className="overflow-auto max-h-96 lg:max-h-none lg:flex-1">
                          <img src={`data:image/png;base64,${detail.screenshot_b64}`} alt="Final state of the employer's application page" className="w-full" />
                        </div>
                      </section>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
